export type BioanalysisReadiness = "ready" | "partial" | "insufficient";
export type BioanalysisEvidenceStatus = "confirmed" | "review";
export type ValidationApplicability = "required" | "conditional";

export interface BioanalysisAnalyte {
  id: string;
  nameCn: string;
  nameEn: string;
  role: string;
  evidence: string;
  status: BioanalysisEvidenceStatus;
}

export interface BioanalysisMethodEvidence {
  id: string;
  pmid: string;
  title: string;
  journal: string;
  year: string;
  studyType: string;
  matrix: string[];
  platform: string;
  preparation: string[];
  sampleVolume: string;
  lloq: string;
  calibrationRange: string;
  runTime: string;
  explicitEvidence: string[];
  url: string;
  doiUrl: string;
  fullTextUrl: string;
  relevance: number;
}

export interface BioanalysisValidationItem {
  id: string;
  item: string;
  applicability: ValidationApplicability;
  objective: string;
  evidenceRequired: string;
  sourceTitle: string;
  sourceUrl: string;
}

export interface BioanalysisPhaseOne {
  readiness: BioanalysisReadiness;
  score: number;
  summary: string;
  analytes: BioanalysisAnalyte[];
  methodEvidence: BioanalysisMethodEvidence[];
  validationChecklist: BioanalysisValidationItem[];
  gaps: string[];
  limitations: string[];
}

export interface BuildBioanalysisPhaseOneInput {
  drugName: { cn: string; en: string; inn: string };
  isCombination?: boolean;
  components?: Array<{ cn: string; en: string }>;
  data?: Record<string, unknown>;
}

const FDA_M10_URL =
  "https://www.fda.gov/regulatory-information/search-fda-guidance-documents/m10-bioanalytical-method-validation-and-study-sample-analysis";
const EMA_M10_URL =
  "https://www.ema.europa.eu/en/ich-m10-bioanalytical-method-validation-scientific-guideline";
const ICH_M10_FAQ_URL =
  "https://database.ich.org/sites/default/files/M10_Guideline_FAQs_2022_0616_0.pdf";

const VALIDATION_CHECKLIST: BioanalysisValidationItem[] = [
  {
    id: "selectivity",
    item: "选择性与特异性",
    applicability: "required",
    objective: "确认内源性物质、代谢物和合并用药不干扰分析物及内标响应。",
    evidenceRequired: "代表性空白基质、溶血/高脂等适用基质及潜在干扰物的色谱或响应数据。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: FDA_M10_URL,
  },
  {
    id: "calibration",
    item: "校准曲线与定量范围",
    applicability: "required",
    objective: "建立覆盖预期研究样本浓度的可追溯定量范围。",
    evidenceRequired: "校准模型、标准点设置、LLOQ/ULOQ、回算浓度及接受情况。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: EMA_M10_URL,
  },
  {
    id: "accuracy_precision",
    item: "准确度与精密度",
    applicability: "required",
    objective: "证明批内和批间结果满足预设接受标准。",
    evidenceRequired: "LLOQ、低、中、高 QC 的批内/批间准确度和精密度汇总。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: FDA_M10_URL,
  },
  {
    id: "matrix_effect",
    item: "基质效应",
    applicability: "required",
    objective: "评估不同来源基质对离子化或检测响应的影响。",
    evidenceRequired: "多个独立基质来源的基质因子或等效评估，以及内标归一化结果。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: EMA_M10_URL,
  },
  {
    id: "carryover",
    item: "残留",
    applicability: "required",
    objective: "确认高浓度样本后残留不会影响后续样本定量。",
    evidenceRequired: "ULOQ 或高浓度样本后空白样本的分析物与内标响应。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: FDA_M10_URL,
  },
  {
    id: "dilution",
    item: "稀释可靠性",
    applicability: "conditional",
    objective: "验证高于 ULOQ 的研究样本经稀释后仍可准确测定。",
    evidenceRequired: "计划采用稀释倍数下的准确度、精密度和稀释基质说明。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: EMA_M10_URL,
  },
  {
    id: "stability",
    item: "稳定性",
    applicability: "required",
    objective: "覆盖采样、处理、运输、储存和进样全过程。",
    evidenceRequired: "短期、长期、冻融、处理后及储备液/工作液稳定性；条件应覆盖研究实际。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: FDA_M10_URL,
  },
  {
    id: "partial_cross_validation",
    item: "部分验证与交叉验证",
    applicability: "conditional",
    objective: "方法变更、实验室切换或多方法合并时证明结果可比。",
    evidenceRequired: "变更影响评估、部分验证项目，以及实验室/平台间交叉验证结果。",
    sourceTitle: "ICH M10 Questions and Answers",
    sourceUrl: ICH_M10_FAQ_URL,
  },
  {
    id: "isr",
    item: "研究样本再分析（ISR）",
    applicability: "required",
    objective: "用真实研究样本确认方法在研究实施阶段的重现性。",
    evidenceRequired: "ISR 样本选择规则、覆盖范围、原测与重测差异及通过比例。",
    sourceTitle: "ICH M10 Bioanalytical Method Validation",
    sourceUrl: EMA_M10_URL,
  },
];

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asRecordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> => Boolean(item) && typeof item === "object" && !Array.isArray(item))
    : [];
}

function text(value: unknown): string {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function unique(values: string[]): string[] {
  const seen = new Set<string>();
  return values.filter((value) => {
    const normalized = value.trim();
    const key = normalized.toLocaleLowerCase();
    if (!normalized || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function collectPubmedArticles(pubmed: Record<string, unknown>): Record<string, unknown>[] {
  const candidates = [...asRecordArray(pubmed.articles)];
  asRecordArray(pubmed.evidence_categories).forEach((category) => {
    candidates.push(...asRecordArray(category.articles));
  });

  const seen = new Set<string>();
  return candidates.filter((article) => {
    const key = text(article.pmid) || text(article.doi).toLocaleLowerCase() || text(article.title).toLocaleLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function findLabels(haystack: string, patterns: Array<[RegExp, string]>): string[] {
  return unique(patterns.filter(([pattern]) => pattern.test(haystack)).map(([, label]) => label));
}

function firstCapturedValue(haystack: string, patterns: RegExp[]): string {
  for (const pattern of patterns) {
    const match = haystack.match(pattern);
    if (match?.[1]) return match[1].replace(/\s+/g, " ").trim();
  }
  return "";
}

function extractMethodEvidence(article: Record<string, unknown>, index: number): BioanalysisMethodEvidence | null {
  const title = text(article.title);
  const abstract = text(article.abstract);
  const tags = Array.isArray(article.tags) ? article.tags.map(text).join(" ") : text(article.tags);
  const matchedTerms = Array.isArray(article.matched_terms)
    ? article.matched_terms.map(text).join(" ")
    : text(article.matched_terms);
  const studyType = text(article.study_type || article.category);
  const searchable = [title, abstract, tags, matchedTerms, studyType].join(" ");

  const platform = findLabels(searchable, [
    [/\b(?:UPLC|UHPLC)[-–—\s]?(?:MS\/MS|MS-MS|tandem mass spectrometr(?:y|ic))\b/i, "UPLC-MS/MS"],
    [/\bLC[-–—\s]?(?:MS\/MS|MS-MS|tandem mass spectrometr(?:y|ic))\b/i, "LC-MS/MS"],
    [/\bliquid chromatography[-–—\s]?(?:tandem )?mass spectrometr(?:y|ic)\b/i, "LC-MS/MS"],
    [/\b(?:UPLC|UHPLC)\b/i, "UPLC/UHPLC"],
    [/\b(?:HPLC|high[- ]performance liquid chromatography)\b/i, "HPLC"],
    [/\b(?:GC[-–—\s]?MS|gas chromatography[-–—\s]?mass spectrometr(?:y|ic))\b/i, "GC-MS"],
    [/\b(?:ligand binding assay|LBA)\b/i, "LBA"],
  ])[0] || "";

  const matrix = findLabels(searchable, [
    [/\b(?:human |rat |mouse |dog |rabbit )?plasma\b/i, "血浆"],
    [/\bserum\b/i, "血清"],
    [/\bwhole blood\b/i, "全血"],
    [/\burine\b/i, "尿液"],
    [/\b(?:cerebrospinal fluid|CSF)\b/i, "脑脊液"],
    [/\bsaliva\b/i, "唾液"],
  ]);

  const preparation = findLabels(searchable, [
    [/\b(?:protein precipitation|precipitation of proteins)\b/i, "蛋白沉淀"],
    [/\b(?:liquid[- ]liquid extraction|LLE)\b/i, "液液萃取"],
    [/\b(?:solid[- ]phase extraction|SPE)\b/i, "固相萃取"],
    [/\b(?:dilute[- ]and[- ]shoot|dilution and injection)\b/i, "稀释后进样"],
  ]);

  const sampleVolume = firstCapturedValue(searchable, [
    /(?:sample volume|plasma volume|serum volume|aliquot(?: of)?)[^.;]{0,28}?(\d+(?:\.\d+)?\s*(?:µL|μL|uL|mL))/i,
    /(\d+(?:\.\d+)?\s*(?:µL|μL|uL|mL))\s+(?:of\s+)?(?:plasma|serum|whole blood|urine)/i,
  ]);
  const lloq = firstCapturedValue(searchable, [
    /(?:LLOQ|lower limit of quant(?:ification|itation))[^.;]{0,35}?(\d+(?:\.\d+)?\s*(?:pg|ng|µg|μg|ug)\s*\/\s*(?:mL|L))/i,
  ]);
  const calibrationRange = firstCapturedValue(searchable, [
    /(?:calibration|linear|quantification|quantitation|analytical)?\s*range[^.;]{0,35}?(\d+(?:\.\d+)?\s*(?:-|–|—|to)\s*\d+(?:\.\d+)?\s*(?:pg|ng|µg|μg|ug)\s*\/\s*(?:mL|L))/i,
  ]);
  const runTime = firstCapturedValue(searchable, [
    /(?:run time|runtime|chromatographic run)[^.;]{0,24}?(\d+(?:\.\d+)?\s*(?:min|minutes?))/i,
  ]);

  const directMethodTerms = findLabels(searchable, [
    [/\bbioanalytical(?: method)?\b/i, "生物分析方法"],
    [/\bmethod validation\b/i, "方法验证"],
    [/\bvalidated (?:assay|method)\b/i, "已验证方法"],
    [/\bquantif(?:y|ied|ication|itative)\b/i, "定量分析"],
    [/\bdetermination of\b/i, "测定方法"],
    [/\bmass spectrometr(?:y|ic)\b/i, "质谱检测"],
  ]);

  const relevanceSignals = [
    platform,
    matrix.length > 0 ? "matrix" : "",
    preparation.length > 0 ? "preparation" : "",
    directMethodTerms.length > 0 ? "method" : "",
    lloq,
    calibrationRange,
  ].filter(Boolean).length;
  if (relevanceSignals < 2 && directMethodTerms.length === 0) return null;

  const explicitEvidence = unique([
    ...directMethodTerms,
    ...matrix.map((value) => `基质：${value}`),
    platform ? `平台：${platform}` : "",
    ...preparation.map((value) => `前处理：${value}`),
    sampleVolume ? `样本体积：${sampleVolume}` : "",
    lloq ? `LLOQ：${lloq}` : "",
    calibrationRange ? `范围：${calibrationRange}` : "",
    runTime ? `运行时间：${runTime}` : "",
  ]);

  return {
    id: text(article.pmid) || text(article.doi) || `method-${index}`,
    pmid: text(article.pmid),
    title: title || "未命名文献",
    journal: text(article.journal),
    year: text(article.year),
    studyType: studyType || "方法学相关文献",
    matrix,
    platform,
    preparation,
    sampleVolume,
    lloq,
    calibrationRange,
    runTime,
    explicitEvidence,
    url: text(article.url),
    doiUrl: text(article.doi_url) || (text(article.doi) ? `https://doi.org/${text(article.doi)}` : ""),
    fullTextUrl: text(article.full_text_url),
    relevance: Number(article.abstract_relevance || 0),
  };
}

function buildAnalytes(input: BuildBioanalysisPhaseOneInput): BioanalysisAnalyte[] {
  const suppliedComponents = input.components || [];
  const candidates = input.isCombination && suppliedComponents.length > 0
    ? suppliedComponents
    : [{ cn: input.drugName.cn, en: input.drugName.inn || input.drugName.en }];

  return candidates.map((component, index) => {
    const nameCn = text(component.cn);
    const nameEn = text(component.en);
    const status: BioanalysisEvidenceStatus = nameEn ? "confirmed" : "review";
    return {
      id: `${nameEn || nameCn || "analyte"}-${index}`,
      nameCn: nameCn || "待确认",
      nameEn: nameEn || "待确认",
      role: input.isCombination ? "待分析活性成分" : "原形药/活性成分",
      evidence: unique([
        nameCn ? `中文名：${nameCn}` : "",
        nameEn ? `英文名/INN：${nameEn}` : "",
      ]).join("；"),
      status,
    };
  });
}

export function buildBioanalysisPhaseOne(input: BuildBioanalysisPhaseOneInput): BioanalysisPhaseOne {
  const pubmed = asRecord(input.data?.pubmed);
  const analytes = buildAnalytes(input);
  const methodEvidence = collectPubmedArticles(pubmed)
    .map(extractMethodEvidence)
    .filter((item): item is BioanalysisMethodEvidence => Boolean(item))
    .sort((a, b) => b.relevance - a.relevance || Number(b.year || 0) - Number(a.year || 0))
    .slice(0, 12);

  const analytesConfirmed = analytes.length > 0 && analytes.every((item) => item.status === "confirmed");
  const evidenceDetailCount = methodEvidence.reduce((count, item) => {
    return count + [item.matrix.length > 0, Boolean(item.platform), item.preparation.length > 0, Boolean(item.lloq), Boolean(item.calibrationRange)].filter(Boolean).length;
  }, 0);
  const methodDetailScore = methodEvidence.length > 0
    ? Math.min(50, 20 + Math.round((evidenceDetailCount / (methodEvidence.length * 5)) * 30))
    : 0;
  const score = Math.min(100, (analytesConfirmed ? 30 : 15) + methodDetailScore + 20);
  const hasActionableMethod = methodEvidence.some((item) => item.matrix.length > 0 && Boolean(item.platform));
  const readiness: BioanalysisReadiness = methodEvidence.length === 0
    ? "insufficient"
    : analytesConfirmed && hasActionableMethod
      ? "ready"
      : "partial";

  const gaps: string[] = [];
  if (!analytesConfirmed) {
    gaps.push("分析物中英文身份尚未全部确认，应先锁定 INN、盐型及复方各活性成分。" );
  }
  if (methodEvidence.length === 0) {
    gaps.push("现有 PubMed 结果中未识别到明确的生物分析方法文献，需补充商品名、代谢物名或全文检索。" );
  } else {
    if (!methodEvidence.some((item) => item.matrix.length > 0)) gaps.push("方法证据未明确报告生物基质。" );
    if (!methodEvidence.some((item) => item.platform)) gaps.push("方法证据未明确报告检测平台。" );
    if (!methodEvidence.some((item) => item.lloq)) gaps.push("摘要层证据未明确报告 LLOQ，需查阅全文或验证报告。" );
  }
  gaps.push("当前数据未提供经确认的活性代谢物清单；应结合说明书和临床药理审评判断是否需要同步测定。" );

  const summary = readiness === "ready"
    ? `已确认 ${analytes.length} 个分析对象，并识别 ${methodEvidence.length} 篇方法学相关文献；可进入全文核对和方法适用性评估。`
    : readiness === "partial"
      ? `已获得部分分析对象和方法证据，但关键方法参数仍需通过全文或验证资料补齐。`
      : "已建立 ICH M10 核查框架，但当前结果不足以形成可执行的生物样本分析方法建议。";

  return {
    readiness,
    score,
    summary,
    analytes,
    methodEvidence,
    validationChecklist: VALIDATION_CHECKLIST.map((item) => ({ ...item })),
    gaps: unique(gaps),
    limitations: [
      "仅提取题名、摘要和结构化元数据中明确出现的参数；空白项不代表研究未实施。",
      "文献参数仅用于方法开发调研，不能替代实验室基质效应、稳定性和方法验证实验。",
      "LLOQ、线性范围、样本体积及前处理等关键条件必须回到全文和原始方法资料复核。",
    ],
  };
}

